import { ArrowLeft, Building2, Clock3, ExternalLink, Handshake, Mail, MapPin, MessageSquare, Phone, Scale, UserPlus, UserRound } from "lucide-react";
import { useState } from "react";
import { useMutation } from "convex/react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar, Button, Card, EmptyState, PageHeader, Section, Tag } from "../components/ui";
import { convexApi } from "../convexApi";
import { labelForRequestStatus, labelForRequestType } from "../lib/cooperation";
import { getFirmPracticeAreas, getUserPracticeAreas, hasTechnicalRole, isAdmin, isFullMemberUser } from "../lib/domain";
import { realParticipationPoints } from "../lib/governanceDemo";
import { buildCooperationReputation } from "../lib/reputation";
import { calculateMembershipParticipationBonus, calculateVotingEntitlement } from "../lib/voting";
import type { Firm, LexData, User } from "../lib/types";

type AdmissionTargetCategory = "Full Member" | "Associate Member" | "Expert Contributor";
type AdmissionPersistence = {
  proposeFirmAdmission: (input: { firmId: string; targetCategory: AdmissionTargetCategory }) => Promise<unknown>;
  finalizeFirmAdmission: (firmId: string) => Promise<unknown>;
};

export function MemberProfilePage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveMemberProfilePage data={data} />;
  return <MemberProfileContent data={data} />;
}

function LiveMemberProfilePage({ data }: { data: LexData }) {
  const proposeFirmAdmission = useMutation(convexApi.admission.proposeFirmAdmission);
  const finalizeFirmAdmission = useMutation(convexApi.admission.finalizeFirmAdmission);
  const persistence: AdmissionPersistence = {
    proposeFirmAdmission: (input) => proposeFirmAdmission({ firmId: input.firmId as never, targetCategory: input.targetCategory }),
    finalizeFirmAdmission: (firmId) => finalizeFirmAdmission({ firmId: firmId as never }),
  };
  return <MemberProfileContent data={data} persistence={persistence} />;
}

function MemberProfileContent({ data, persistence }: { data: LexData; persistence?: AdmissionPersistence }) {
  const { firmId } = useParams();
  const [searchParams] = useSearchParams();
  const firm = data.firms.find((item) => item.id === firmId);
  if (!firm) return <Navigate to="/members-directory" replace />;

  const highlightedUserId = searchParams.get("user");
  const users = data.users.filter((user) => user.firmId === firm.id && user.active);
  const highlightedUser = users.find((user) => user.id === highlightedUserId) ?? users.find((user) => user.representativeType === "principal") ?? users[0];
  const areas = getFirmPracticeAreas(firm.id, data.firmPracticeAreas, data.practiceAreas);
  const highlightedAreas = highlightedUser ? getUserPracticeAreas(highlightedUser.id, data.userPracticeAreas, data.practiceAreas) : [];
  const authoredPosts = data.discussions.filter((discussion) => discussion.createdByFirmId === firm.id);
  const contributedPostIds = new Set(data.contributions.filter((item) => item.createdByFirmId === firm.id).map((item) => item.discussionId));
  const contributionCount = data.contributions.filter((item) => item.createdByFirmId === firm.id).length;
  const latestPost = authoredPosts.concat(data.discussions.filter((discussion) => contributedPostIds.has(discussion.id))).sort((a, b) => b.lastActivityAt - a.lastActivityAt)[0];
  const cooperation = data.cooperationRequests.filter((request) => request.requesterFirmId === firm.id || request.targetFirmId === firm.id);
  const activeCooperation = cooperation.filter((request) => !["completed", "closed", "declined", "conflict_declared", "expired", "withdrawn"].includes(request.status));
  const currentWeight = memberVotingWeight(data, firm);
  const reputation = buildCooperationReputation(data, firm.id);
  const viewer = data.viewer;
  const candidateNotFullMember = firm.membershipCategory !== "Full Member";
  const admissionInProgress = Boolean(firm.admissionProposalId);
  const canPropose = Boolean(persistence) && isFullMemberUser(viewer) && data.viewerFirm?.id !== firm.id && candidateNotFullMember && !admissionInProgress;
  const canFinalize = Boolean(persistence) && (isAdmin(viewer) || viewer.role === "Membership and Balance Committee" || hasTechnicalRole(viewer, "workspace_manager")) && admissionInProgress;

  return (
    <>
      <Link to="/members-directory" className="mb-4 inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm font-medium text-secondary hover:bg-hover">
        <ArrowLeft className="h-4 w-4" />
        Back to members directory
      </Link>
      <PageHeader
        title={firm.name}
        subtitle={`${firm.jurisdiction} · ${firm.membershipCategory} · ${firm.lawyerCount} lawyers`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to={`/members-directory?cooperate=${firm.id}`} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-4 text-sm font-semibold text-ink hover:bg-hover">
              <Handshake className="h-4 w-4" />
              Request expertise
            </Link>
            <Link to={`/members-directory?message=${firm.id}`} className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-4 text-sm font-semibold text-ink hover:bg-hover">
              <Mail className="mr-2 inline h-4 w-4" />
              Send a message
            </Link>
          </div>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid gap-5">
          <Card>
            <div className="mb-5 rounded-xl border border-divider bg-canvas p-3" aria-label="Member identity">
              <div className="flex items-center gap-3">
                <Avatar name={highlightedUser?.name ?? firm.name} imageUrl={highlightedUser?.avatarUrl} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink">{highlightedUser?.name ?? firm.name}</div>
                  <div className="mt-0.5 text-sm text-secondary">{highlightedUser?.role ?? "Member"} · {firm.name}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {highlightedAreas.length ? highlightedAreas.map((area) => <Tag key={area.id}>{area.name}</Tag>) : <Tag>No lawyer practice areas selected</Tag>}
                  </div>
                </div>
                <Avatar name={firm.name} imageUrl={firm.logoUrl ?? firm.avatarUrl} size="md" shape="rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Avatar name={firm.name} imageUrl={firm.logoUrl ?? firm.avatarUrl} shape="rounded" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap gap-2">
                  <Tag>{firm.membershipCategory}</Tag>
                  <Tag>{firm.status}</Tag>
                  <Tag>{firm.acceptsCooperationRequests === false ? "Not accepting exchange requests" : "Accepts exchange requests"}</Tag>
                </div>
                <p className="mt-4 text-sm leading-6 text-secondary">{firm.independenceStatement || "No firm bio has been added yet."}</p>
                <div className="mt-4 grid gap-2 text-sm text-secondary">
                  <ContactLine icon={MapPin} value={firm.address ?? firm.jurisdiction} />
                  <ContactLine icon={ExternalLink} value={firm.websiteUrl} href={firm.websiteUrl ?? undefined} />
                  <ContactLine icon={Phone} value={firm.telephone} />
                  <ContactLine icon={Mail} value={firm.contactEmail} href={firm.contactEmail ? `mailto:${firm.contactEmail}` : undefined} />
                </div>
              </div>
            </div>
          </Card>

          <Section title="People">
            <div className="grid gap-3 md:grid-cols-2">
              {users.map((user) => (
                <Card key={user.id} className={user.id === highlightedUser?.id ? "border-primary" : ""}>
                  {(() => {
                    const userAreas = getUserPracticeAreas(user.id, data.userPracticeAreas, data.practiceAreas);
                    return (
                  <div className="flex items-center gap-3">
                    <div className="relative w-fit shrink-0">
                      <Avatar name={user.name} imageUrl={user.avatarUrl} />
                      <span className="absolute -bottom-1 -right-1 rounded-lg bg-surface p-0.5 shadow-sm">
                        <Avatar name={firm.name} imageUrl={firm.logoUrl ?? firm.avatarUrl} size="sm" shape="rounded" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{user.name}</div>
                      <div className="text-sm text-secondary">{user.role}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {userAreas.slice(0, 3).map((area) => <Tag key={area.id}>{area.name}</Tag>)}
                      </div>
                    </div>
                    <Tag>{user.representativeType}</Tag>
                  </div>
                    );
                  })()}
                </Card>
              ))}
            </div>
          </Section>

          <Section title="Practice and expertise exchange profile">
            <Card>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 text-sm font-semibold">Firm practice areas</div>
                  <div className="flex flex-wrap gap-2">
                    {areas.length > 0 ? areas.map((area) => <Tag key={area.id}>{area.name}</Tag>) : <Tag>No practice areas selected</Tag>}
                  </div>
                </div>
                <div>
                  <div className="mb-2 text-sm font-semibold">Expertise exchange interests</div>
                  <div className="flex flex-wrap gap-2">
                    {firm.cooperationInterests?.length ? firm.cooperationInterests.map((type) => <Tag key={type}>{labelForRequestType(type)}</Tag>) : <Tag>Not specified</Tag>}
                  </div>
                </div>
              </div>
              <div className="mt-4 grid gap-2 text-sm text-secondary">
                {firm.languages?.length ? (
                  <div>
                    <div className="mb-1 font-medium text-ink">Languages</div>
                    <div className="flex flex-wrap gap-2">{firm.languages.map((language) => <Tag key={language}>{language}</Tag>)}</div>
                  </div>
                ) : null}
                {firm.preferredContactMode ? <div>Preferred contact: {firm.preferredContactMode}</div> : null}
                {firm.responseTimeIndicator ? <div>Response expectation: {firm.responseTimeIndicator}</div> : null}
                {firm.conflictCheckInstructions ? <div>Professional responsibility: {firm.conflictCheckInstructions}</div> : null}
              </div>
            </Card>
          </Section>

          <Section title="Latest activity">
            {latestPost ? (
              <Card>
                <div className="flex items-start gap-3">
                  <MessageSquare className="mt-1 h-5 w-5 text-primary" />
                  <div className="min-w-0 flex-1">
                    <Link to={`/discussions?discussion=${latestPost.id}`} className="font-semibold text-ink hover:underline">{latestPost.title}</Link>
                    <p className="mt-1 text-sm leading-6 text-secondary">{latestPost.body}</p>
                  </div>
                  <Tag>{relativeTime(latestPost.lastActivityAt)}</Tag>
                </div>
              </Card>
            ) : (
              <EmptyState>No member activity has been recorded yet.</EmptyState>
            )}
          </Section>
        </div>

        <div className="grid gap-4 self-start">
          <StatCard icon={UserRound} label="Highlighted member" value={highlightedUser?.name ?? firm.name} detail={highlightedUser?.email ?? ""} />
          <StatCard icon={Building2} label="Firm and jurisdiction" value={firm.name} detail={firm.jurisdiction} />
          <StatCard icon={MessageSquare} label="Posts and contributions" value={`${authoredPosts.length} posts`} detail={`${contributionCount} contributions`} />
          <StatCard icon={Scale} label="Current voting weight" value={currentWeight.toFixed(2)} detail="Calculated from membership category and validated participation only." />
          <StatCard icon={Handshake} label="Expertise exchange status" value={firm.acceptsCooperationRequests === false ? "Not accepting" : "Accepting requests"} detail={`${activeCooperation.length} active request${activeCooperation.length === 1 ? "" : "s"}`} />
          {canPropose || admissionInProgress ? (
            <AdmissionCard firm={firm} canPropose={canPropose} canFinalize={canFinalize} admissionInProgress={admissionInProgress} persistence={persistence} />
          ) : null}
          {reputation && (reputation.requestsReceived > 0 || reputation.completedCooperations > 0) ? (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                <Handshake className="h-4 w-4 text-emerald-700" />
                Trusted cooperation
              </div>
              <div className="text-sm text-secondary">{reputation.responsivenessLabel}{reputation.averageRating !== null ? ` · ${reputation.averageRating.toFixed(1)}★` : ""}</div>
              <div className="mt-1 text-[12px] text-secondary">
                {reputation.completedCooperations} completed · {reputation.debriefsSubmitted} debrief{reputation.debriefsSubmitted === 1 ? "" : "s"} shared
              </div>
              {reputation.trustSignals.length > 0 ? <div className="mt-2 flex flex-wrap gap-1.5">{reputation.trustSignals.map((signal) => <Tag key={signal}>{signal}</Tag>)}</div> : null}
              <div className="mt-2 text-[11px] text-muted">Reputation only — cooperation does not affect votes.</div>
            </Card>
          ) : null}
          <Card>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Clock3 className="h-4 w-4 text-primary" />
              Recent expertise exchange
            </div>
            <div className="grid gap-2">
              {cooperation.slice(0, 3).map((request) => (
                <Link key={request.id} to="/expertise-exchange" className="rounded-lg border border-line bg-canvas px-3 py-2 text-sm hover:bg-hover">
                  <span className="block font-medium text-ink">{request.title}</span>
                  <span className="mt-1 block text-xs text-secondary">{labelForRequestType(request.requestType)} · {labelForRequestStatus(request.status)}</span>
                </Link>
              ))}
              {cooperation.length === 0 ? <div className="text-sm text-secondary">No expertise exchange requests involving this firm yet.</div> : null}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function AdmissionCard({ firm, canPropose, canFinalize, admissionInProgress, persistence }: { firm: Firm; canPropose: boolean; canFinalize: boolean; admissionInProgress: boolean; persistence?: AdmissionPersistence }) {
  const [targetCategory, setTargetCategory] = useState<AdmissionTargetCategory>("Full Member");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const propose = async () => {
    if (!persistence) return;
    setBusy(true);
    setMessage(null);
    try {
      await persistence.proposeFirmAdmission({ firmId: firm.id, targetCategory });
      setMessage(`Admission vote opened to admit ${firm.name} as ${targetCategory}.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "The admission vote could not be opened.");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    if (!persistence) return;
    setBusy(true);
    setMessage(null);
    try {
      await persistence.finalizeFirmAdmission(firm.id);
      setMessage(`${firm.name} has been admitted.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Admission could not be finalised.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        <UserPlus className="h-4 w-4 text-primary" />
        Network admission
      </div>
      {admissionInProgress ? (
        <>
          <div className="text-sm text-secondary">An admission vote is in progress{firm.proposedMembershipCategory ? ` (${firm.proposedMembershipCategory})` : ""}.</div>
          {canFinalize ? (
            <Button type="button" className="mt-3 w-full" disabled={busy} onClick={() => void finalize()}>Finalise admission</Button>
          ) : (
            <div className="mt-2 text-[12px] text-muted">Finalise once the vote is certified adopted.</div>
          )}
        </>
      ) : canPropose ? (
        <>
          <div className="text-sm text-secondary">Propose this firm for network membership. Members vote over 7 days.</div>
          <label className="mt-3 grid gap-1.5 text-sm font-medium text-ink">
            Target category
            <select className="min-h-11 rounded-lg border border-line bg-surface px-3 font-normal" value={targetCategory} onChange={(event) => setTargetCategory(event.target.value as AdmissionTargetCategory)}>
              <option value="Full Member">Full Member</option>
              <option value="Associate Member">Associate Member</option>
              <option value="Expert Contributor">Expert Contributor</option>
            </select>
          </label>
          <Button type="button" className="mt-3 w-full" disabled={busy} onClick={() => void propose()}>Propose for membership</Button>
        </>
      ) : null}
      {message ? <div className="mt-2 text-[12px] text-secondary">{message}</div> : null}
    </Card>
  );
}

function memberVotingWeight(data: LexData, firm: Firm) {
  const period = "2026-H1";
  const bonus = calculateMembershipParticipationBonus(realParticipationPoints(data), firm.id, period, firm.membershipCategory);
  return calculateVotingEntitlement({
    memberEntityId: firm.id,
    membershipCategory: firm.membershipCategory,
    period,
    participationBonus: bonus,
    decisionType: "ordinary",
    associateLimitedVotingEnabled: true,
    expertLimitedVotingEnabled: true,
  }).totalWeight;
}

function ContactLine({ icon: Icon, value, href }: { icon: typeof MapPin; value?: string | null; href?: string }) {
  if (!value) return null;
  const content = (
    <span className="inline-flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted" />
      {value}
    </span>
  );
  return href ? <a className="hover:text-primary hover:underline" href={href}>{content}</a> : <div>{content}</div>;
}

function StatCard({ icon: Icon, label, value, detail }: { icon: typeof UserRound; label: string; value: string; detail: string }) {
  return (
    <Card>
      <div className="flex items-start gap-3">
        <Icon className="mt-1 h-5 w-5 text-primary" />
        <div className="min-w-0">
          <div className="text-xs font-medium text-muted">{label}</div>
          <div className="mt-1 font-semibold text-ink">{value}</div>
          {detail ? <div className="mt-1 text-sm leading-5 text-secondary">{detail}</div> : null}
        </div>
      </div>
    </Card>
  );
}

function relativeTime(time: number) {
  const hours = Math.max(1, Math.round((Date.now() - time) / 3_600_000));
  return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}
